import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, firstValueFrom, map, retry } from 'rxjs';
import { ICustomerPortfolio } from 'src/app/core/interfaces/onboarding/customer-portfolio/customer-portfolio.interface';
import * as CustomerDetailsAction from 'src/app/core/store/onboarding/actions/customer-portfolio.actions';
import { environment } from 'src/environments/environment';
import { handleHttpError } from '../../interceptors/handle-http-error';
import { IApiResponse } from '../../interfaces/api/api-response.interface';
import { IIdValuePair } from '../../interfaces/common/common/id-value-pair.interface';
import { IAdditionalCreditInfo } from '../../interfaces/onboarding/customer-portfolio/additional-credit-info.interface';
import { IClientCustomer } from '../../interfaces/onboarding/customer-portfolio/client-customer.interface';
import { ICreditCover } from '../../interfaces/onboarding/customer-portfolio/credit-cover.interface';
import { ICustomerPortfolioExtractInfo } from '../../interfaces/onboarding/customer-portfolio/customer-portfolio-extract-info.interface';
import { ICustomerRequest } from '../../interfaces/onboarding/customer-portfolio/customer-request.interface';
import { IAppState } from '../../store/common/interfaces/app-state.interface';
import { selectCustomerDetails } from '../../store/onboarding/reducers+selectors/customer-portfolio.selector';
import {
  applyEnrollmentIdToQueryParams,
  applyUserProfileId,
} from '../../ui-helpers/functions/user-context-url.func';
import { KendoNotificationService } from '../common/kendo-notification.service';
import { LoggerService } from '../common/logger.service';

@Injectable({
  providedIn: 'root',
})
export class CustomerPortfolioService {
  private apiUrl = environment.apiUrl;

  public apiClientCustomer: IClientCustomer[];
  invoiceData: any;
  clientCustomerinfo: any;
  payload: ICustomerPortfolio;
  public apiResponse: IApiResponse;
  public customerPortfolio: ICustomerPortfolio;
  private customerControllerSuffix = 'Onboarding/Onboarding/customer/';
  private sharedControllerSuffix = 'Onboarding/Onboarding/shared/';
  private customerExtractInfo: ICustomerPortfolioExtractInfo;
  constructor(
    private httpClient: HttpClient,
    private store: Store<IAppState>,
    private logger: LoggerService,
    private kns: KendoNotificationService,
  ) {}

  async deleteCustomerInfo(clientcustomerId: string): Promise<IApiResponse> {
    const formData: any = new FormData();
    formData.append('ClientCustomerStoredId', clientcustomerId);
    const url = `${this.apiUrl}${this.customerControllerSuffix}delete-customer-details`;
    const result = await firstValueFrom(
      this.httpClient.post<IApiResponse>(url, formData).pipe(),
    );
    if (result === null || result.data === null) this.apiResponse;
    else this.apiResponse = result.data as unknown as IApiResponse;
    return this.apiResponse;
  }

  async extractCustomerInfo(
    model: ICustomerPortfolio,
  ): Promise<ICustomerPortfolioExtractInfo> {
    const formData: any = new FormData();
    formData.append('File', model.file);
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.customerControllerSuffix}extract-customer-info`;
    const result = await firstValueFrom(
      this.httpClient.post<IApiResponse>(url, formData, { params }).pipe(),
    );

    if (result === null || result.data === null) this.customerExtractInfo;
    else
      this.customerExtractInfo =
        result.data as unknown as ICustomerPortfolioExtractInfo;

    this.customerExtractInfo = result.data as ICustomerPortfolioExtractInfo;

    this.invoiceData = this.apiResponse;
    return this.customerExtractInfo;
  }

  async getCustomerInfo() {
    if (this.invoiceData) {
      return this.invoiceData;
    }
    return null;
  }

  async getGridCustomerData(): Promise<IClientCustomer[]> {
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.customerControllerSuffix}get-customer`;
    const result = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url, { params }).pipe(
        map((result) => result.data as IClientCustomer[]),
        retry(3), // retry a failed request up to 3 times
        catchError((error: HttpErrorResponse) =>
          handleHttpError(error, this.logger, this.kns),
        ), // handle error if all request attempts fail
      ),
    );

    // Dispatch the ClearStore action
    this.store.dispatch(CustomerDetailsAction.ClearCustomerAction());
    return result;
  }

  async submitCustomerPortfolioData(
    customerPortfolio: ICustomerPortfolio,
  ): Promise<ICustomerPortfolio> {
    const formData: any = new FormData();
    formData.append('CustomerRefNumber', customerPortfolio.CustomerRefNumber);
    formData.append('CustomerName', customerPortfolio.customerName);

    formData.append(
      'ClientCustomerStoredId',
      customerPortfolio.clientCustomerId,
    );
    formData.append('BillToAddress1', customerPortfolio.billingAddress1);
    formData.append('BillToAddress2', customerPortfolio.billingAddress2);
    formData.append('BillToCity', customerPortfolio.billToCity);

    formData.append('BillToState', customerPortfolio.billToState);
    formData.append('BillToZip', customerPortfolio.billToZip);

    formData.append('BillToCountry', customerPortfolio.billToCountry);

    formData.append('PhysicalAddress1', customerPortfolio.physicalToAddress1);
    formData.append('PhysicalAddress2', customerPortfolio.physicalToAddress2);
    formData.append('PhysicalCity', customerPortfolio.physicalToCity);
    formData.append('PhysicalState', customerPortfolio.physicalToState);
    formData.append('PhysicalZip', customerPortfolio.physicalToZip);
    formData.append('PhysicalCountry', customerPortfolio.physicalToCountry);

    formData.append('HQAddress1', customerPortfolio.headquarterAddress1);
    formData.append('HQAddress2', customerPortfolio.headquarterAddress2);
    formData.append('HQCity', customerPortfolio.headquarterCity);
    formData.append('HQState', customerPortfolio.headquarterState);
    formData.append('HQZip', customerPortfolio.headquarterZip);
    formData.append('HQCountry', customerPortfolio.headquarterCountry);
    formData.append(
      'FederalDocumentNumber',
      customerPortfolio.federalDocumentNumber,
    );
    formData.append('File', customerPortfolio.file);
    formData.append('IsFederalGovernmentContract', customerPortfolio.isFederal);
    formData.append(
      'LegalBillToIsLikelyBranch',
      customerPortfolio.isHeadquarterAddress,
    );
    formData.append('MonthlyAmount', customerPortfolio.monthlyInvoiceAmount);
    formData.append('StandardTerms', customerPortfolio.invoiceTerm.value);
    formData.append('StandardTermsId', customerPortfolio.invoiceTerm.id);

    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.customerControllerSuffix}save-customer-details`;
    const result = await firstValueFrom(
      this.httpClient.post<IApiResponse>(url, formData, { params }),
    );

    if (result === null || result.data === null) return this.customerPortfolio;

    this.customerPortfolio = result.data as ICustomerPortfolio;
    if (customerPortfolio.invoiceTerm.value !== '') {
      this.invoiceData = null;
    }
    const clientCustomerId = this.customerPortfolio.clientCustomerStoredId;

    // saving to store
    this.store.dispatch(
      CustomerDetailsAction.FederalDocumentNumberAction({
        federalDocumentNumber: this.customerPortfolio.federalDocumentNumber,
      }),
    );

    // saving to store
    this.store.dispatch(
      CustomerDetailsAction.ClientCustomerIdAction({
        clientCustomerId: this.customerPortfolio.clientCustomerStoredId,
      }),
    );

    return this.customerPortfolio;
  }

  async getEffectiveCreditCover(
    clientCustomerId: number,
  ): Promise<ICreditCover> {
    const url = `${this.apiUrl}${this.customerControllerSuffix}effective-credit-cover/${clientCustomerId}`;
    const response = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url),
    );
    return response.data as ICreditCover;
  }

  async getAdditionalCreditInfo(
    clientCustomerId: number,
  ): Promise<IAdditionalCreditInfo> {
    const url = `${this.apiUrl}${this.customerControllerSuffix}additional-credit-info/${clientCustomerId}`;
    const response = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url),
    );
    if ((response.data as IAdditionalCreditInfo).customerInfoRequested === null)
      response.data.customerInfoRequested =
        'No additional information is being requested from you at this time.';
    if ((response.data as IAdditionalCreditInfo).customerInfoProvided === null)
      response.data.customerInfoProvided = '';
    return response.data as IAdditionalCreditInfo;
  }

  async setAdditionalCreditInfo(
    additionalCreditInfo: IAdditionalCreditInfo,
  ): Promise<IApiResponse> {
    const url = `${this.apiUrl}${this.customerControllerSuffix}additional-credit-info`;
    const response = await firstValueFrom(
      this.httpClient.post<IApiResponse>(url, additionalCreditInfo),
    );
    return response;
  }

  async loadInvoiceTerm(): Promise<IIdValuePair[]> {
    const url = `${this.apiUrl}${this.sharedControllerSuffix}invoice-term`;
    const response = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url),
    );
    if (response === null || response.data === null) return [];
    return this.mapInvoiceTerm(response);
  }

  setInvoiceAmount(amount: number) {
    const Amount = amount;
  }

  mapInvoiceAmount(response: IApiResponse): IIdValuePair[] {
    return (
      response.data as { id: number; invoicePerMonthRange: string }[]
    ).map((item) => ({
      id: item.id,
      value: item.invoicePerMonthRange,
    }));
  }

  mapInvoiceTerm(response: IApiResponse): IIdValuePair[] {
    return (response.data as { id: number; term: string }[]).map((item) => ({
      id: item.id,
      value: item.term,
    }));
  }

  async confirmCustomerInfo(
    customerRequest: ICustomerRequest,
  ): Promise<boolean> {
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    params = applyUserProfileId(params, this.store);
    const url = `${this.apiUrl}${this.customerControllerSuffix}submit-indication`;
    const result = await firstValueFrom(
      this.httpClient.post<IApiResponse>(url, customerRequest, { params }),
    );
    if (result === null || result.data === null) return false;

    return result.data;
  }

  private BuildPayload() {
    const properties$ = this.store.select(selectCustomerDetails);
    const subscription = properties$.subscribe((props) => {
      this.payload = {
        userProfileId: -1,
        customerId: props?.clientCustomerId || -1,
        clientCustomerId: props?.clientCustomerId || -1,
        clientCustomerStoredId: props?.clientCustomerId || -1,
        clientId: props?.clientId || -1,
        CustomerRefNumber: props?.CustomerRefNumber || '',
        customerName: props?.customerName || '',
        isFederal: props.isFederal || false,
        billingAddress1: props?.billingAddress?.address1 || '',
        billingAddress2: props?.billingAddress?.address2 || '',
        billToCity: props?.billingAddress?.city || '',
        billToState: props?.billingAddress?.state || '',
        billToZip: props?.billingAddress?.zipcode || '',
        federalDocumentNumber: props.federalDocumentNumber || -1,
        physicalToAddress1: props?.physicalAddress?.address1 || '',
        physicalToAddress2: props?.physicalAddress?.address2 || '',
        physicalToCity: props?.physicalAddress?.city || '',
        physicalToState: props?.physicalAddress?.state || '',
        physicalToZip: props?.physicalAddress?.zipcode || '',

        physicalToCountry: props?.physicalCountry || '',

        headquarterAddress1: props?.headquarterAddress?.address1 || '',
        headquarterAddress2: props?.headquarterAddress?.address2 || '',
        headquarterCity: props?.headquarterAddress?.city || '',
        headquarterState: props?.headquarterAddress?.state || '',
        headquarterZip: props?.headquarterAddress?.zipcode || '',
        headquarterCountry: props?.headquarterCountry || '',
        customerType: props?.customerType || { id: -1, value: '' },
        isHeadquarterAddress: props?.isHeadquarterAddress || false,
        monthlyInvoiceAmount: props?.monthlyInvoiceAmount || '',
        invoiceTerm: props?.invoiceTerm || { id: -1, value: '' },
        billToCountry: props?.billingCountry || '',
        buyerId: props?.buyerId || 0,
        buyerName: props?.buyerName || '',
        buyerAddress: props?.buyerAddress || '',
        buyerCity: props?.buyerCity || '',
        buyerCountryCode: props?.buyerCountryCode || '',
        buyerPostcode: props?.buyerPostcode || '',
        buyerRegion: props?.buyerRegion || '',
        buyerRegionCode: props?.buyerRegionCode || '',
      };
    });
    subscription.unsubscribe();
  }

  public getPayload() {
    this.BuildPayload();

    return this.payload;
  }
}
